import React, { useEffect, useRef, useState } from 'react';
import { IReservationModel } from 'src/entities/reservation';
import { ICalendarBase, ICalendar, randomCalendarItem } from '../../../../entities/calendar';
import { IStepModuleProps, ModuleSearchParam } from '../../../../models/modules';
import { getCalendars, getCalendarsByService } from '../../../../services/api/calendar';
import makeCancelable, { ICancelablePromise } from '../../../../services/cancelablePromise';
import { StepType } from '../../../../services/getStepOrder';
import withSearchParams, { ISearchParamsOptions } from '../withSearchParams';
import CalendarItem, { CalendarItemSkeleton } from './CalendarItem/CalendarItem';
import AvailabilityWarning from 'src/components/AvailabilityWarning';
import { isSame } from 'src/entities/common';
import SelectButton from '../SelectButton';

interface ICalendarSelectProps extends IStepModuleProps {
    shouldGetByService?: boolean;
    hasRandomCalendar?: boolean;
    canUserSelectCalendar?: boolean;
}

const searchParamsOptions: ISearchParamsOptions<ICalendarBase> = {
    paramName: ModuleSearchParam.CalendarId,
};

export const CalendarSelect: React.FC<ICalendarSelectProps> = ({
    isActive,
    isFirst,
    data,
    shouldGetByService,
    hasRandomCalendar,
    canUserSelectCalendar,
    onIsModifiable,
    onLoadingChanged,
    onComplete = () =>
        // eslint-disable-next-line no-console
        console.warn(
            'CalendarSelect module onComplete event is not set, please attach an event handler'
        ),
}) => {
    const prevProps = useRef({ data });
    const calendarsSubscription = useRef<ICancelablePromise<ICalendar[]>>();
    const service = data[StepType.Service];
    const selectedCalendar = data[StepType.Calendar] as ICalendar | undefined;
    const [calendars, setCalendars] = useState<ICalendar[]>([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [prevHasLoaded, setPrevHasLoaded] = useState(false);
    const [shouldPreSelect, setShouldPreSelect] = useState(false);
    const [shouldCheckSelectedCalendar, setShouldCheckSelectedCalendar] = useState(false);
    const [shouldFetchWhenUpdate, setShouldFetchWhenUpdate] = useState(false);
    const hasAnyLogo = calendars.some((calendar) => calendar.PictureLink);

    const selectCalendar = (calendar: ICalendar, hasSkipped?: boolean) => {
        if (isActive) {
            onComplete(createSelectedCalendar(calendar), !canUserSelectCalendar || hasSkipped);
        }
    };

    const checkSelectedCalendar = () => {
        if (!data[StepType.Calendar]) {
            const searchCalendar = getSearchCalendar();
            if (searchCalendar) {
                const calendar = calendars.find((c) => c.Id === +searchCalendar);
                if (calendar) {
                    selectCalendar(calendar);
                } else {
                    setCalendars([]);
                }
            }
        }
    };

    const fetchCalendars = async () => {
        setHasLoaded(false);
        if (shouldGetByService && service) {
            calendarsSubscription.current = makeCancelable<ICalendar[]>(getCalendarsByService(service.Id));
        } else {
            calendarsSubscription.current = makeCancelable<ICalendar[]>(getCalendars());
        }

        calendarsSubscription.current.promise
            .then((calendarList) => {
                if (hasRandomCalendar && calendarList.length > 1) {
                    calendarList = [randomCalendarItem, ...calendarList];
                }
                setCalendars(calendarList);
                setHasLoaded(true);
                setShouldCheckSelectedCalendar(true);
            })
            .catch(() => null);
    };

    const createSelectedCalendar = (calendar: ICalendar): IReservationModel => {
        if (calendar.Id <= 0) {
            return {
                ...data,
                [StepType.Calendar]: undefined,
            };
        }
        return {
            ...data,
            [StepType.Calendar]: {
                ...calendar,
                displayValue: calendar.Name,
            },
        };
    };

    const preSelect = () => {
        if (isActive && hasLoaded && hasSingleEmptyCalendar()) {
            selectCalendar(calendars[0], true);
        }
    };

    const isCalendarEmpty = (calendar: ICalendar) => {
        return !calendar.PictureLink && !calendar.ShortDescription && !calendar.Description;
    };

    const hasSingleEmptyCalendar = () => {
        return calendars.length === 1 && isCalendarEmpty(calendars[0]);
    };

    const getSearchCalendar = () => {
        const searchCalendar = data?.searchParams?.CalendarId;
        return searchCalendar ?? null;
    };

    const handleCanUserSelectCalendar = (): boolean => {
        if (!canUserSelectCalendar) {
            if (onIsModifiable) {
                onIsModifiable(false);
            }
            const searchCalendar = getSearchCalendar();
            if (!searchCalendar) {
                setHasLoaded(true);
                return true;
            }
        }

        return false;
    };

    useEffect(() => {
        if (handleCanUserSelectCalendar()) {
          return;
        }
        if (!isActive) {
          setShouldFetchWhenUpdate(true);
            return;
        }
        fetchCalendars();

        return () => {
            calendarsSubscription.current?.cancel();
        };
    }, []);

    useEffect(() => {
        if (shouldFetchWhenUpdate) {
            fetchCalendars();
            setShouldFetchWhenUpdate(false);
        }

        const serviceChanged = !isSame(
            prevProps.current.data[StepType.Service],
            data[StepType.Service]
        );
        if (shouldGetByService && serviceChanged) {
            if (handleCanUserSelectCalendar()) {
                prevProps.current = { data };
                return;
            }
            fetchCalendars().then(() => setShouldPreSelect(true));
        } else {
            if (canUserSelectCalendar && onIsModifiable && data[StepType.Service]) {
                onIsModifiable(!hasSingleEmptyCalendar());
            }

            setShouldPreSelect(true);
        }

        if (prevHasLoaded !== hasLoaded) {
            onLoadingChanged?.(!hasLoaded);
        }

        prevProps.current = { data };
        setPrevHasLoaded(hasLoaded);
    }, [shouldFetchWhenUpdate, data, shouldGetByService, canUserSelectCalendar, hasLoaded]);

    useEffect(() => {
        if (shouldCheckSelectedCalendar) {
            checkSelectedCalendar();
            setShouldCheckSelectedCalendar(false);
        }
    }, [shouldCheckSelectedCalendar]);

    useEffect(() => {
        if (shouldPreSelect) {
            preSelect();
            setShouldPreSelect(false);
        }
    }, [shouldPreSelect])
    

    if (!hasLoaded) {
        return (
            <>
                <CalendarItemSkeleton />
                <CalendarItemSkeleton />
            </>
        );
    }

    if (!calendars.length) {
        return <AvailabilityWarning type="bookingNotAvailable" />;
    }

    return (
        <section className="calendar-select">
            {calendars.map((calendar) => (
                <CalendarItem
                    key={calendar.Id}
                    calendar={calendar}
                    service={service}
                    button={
                        <SelectButton
                            isFirstStep={!!isFirst}
                            onClick={() => selectCalendar(calendar)}
                            selected={selectedCalendar?.Id === calendar.Id}
                        />
                    }
                    selectCalendar={selectCalendar}
                    withLogoSpace={hasAnyLogo}
                />
            ))}
        </section>
    );
};

export default withSearchParams<ICalendarSelectProps, ICalendarBase>(
    CalendarSelect,
    searchParamsOptions
);
